import React, { useCallback, useRef, useState } from 'react';
import useVariables from 'src/hooks/useVariables/useVariables';
import { showErrorToast } from 'src/components/Toast';
import { getErrorMessage } from 'src/utils/error';
import { MissingVariablesModal } from './MissingVariablesModal';
import { useSaveMissingVariables } from './useSaveMissingVariables';
import { buildMissingVariableEntries, MissingVariableEntry } from './util';

interface PopulateCostFields {
  annualCostFormula?: string | null;
  implementationCostFormula?: string | null;
}

/**
 * Missing-pill click flavour of the wizard (CRPD-7209 QA round 3): opens
 * MissingVariablesModal listing the CONTROL's unpopulated cost variables (both cost
 * formulas — populating one field's set usually resolves the other too), Save persists
 * them (workspace scope, `useSaveMissingVariables` refetch included) and the recalc
 * updates the row/details in place. Nothing is added or linked — no skip/proceed
 * semantics. The consumer renders `modal` in its tree.
 */
export const usePopulateControlCostVariables = () => {
  const { variables } = useVariables({});
  const saveMissingVariables = useSaveMissingVariables({});
  const [entries, setEntries] = useState<MissingVariableEntry[] | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Identity-stable opener reading variables through a ref: consumers capture it inside
  // memoized column defs, and a captured stale closure would silently no-op after the
  // variables finish loading.
  const variablesRef = useRef(variables);
  variablesRef.current = variables;
  const openForCostFields = useCallback((costFields: PopulateCostFields | null | undefined) => {
    const loadedVariables = variablesRef.current;
    // Variables not loaded → entries can't be attributed (scope/currency); the pill
    // can only render off loaded variables anyway, so this is a no-op guard.
    if (!loadedVariables || !costFields) return;
    const built = buildMissingVariableEntries(
      [costFields.annualCostFormula, costFields.implementationCostFormula],
      loadedVariables,
    );
    if (built.length) setEntries(built);
  }, []);

  const modal = entries ? (
    <MissingVariablesModal
      entries={entries}
      destination="populate"
      plural={false}
      saving={isSaving}
      onSaveAndProceed={async (values) => {
        setIsSaving(true);
        try {
          await saveMissingVariables(values);
          setEntries(null);
        } catch (err) {
          // Toast, not an inline line — this modal stacks above arbitrary surfaces.
          showErrorToast(getErrorMessage(err, 'Something went wrong'));
        } finally {
          setIsSaving(false);
        }
      }}
      onSkip={() => setEntries(null)}
      onClose={() => setEntries(null)}
    />
  ) : null;

  return { openForCostFields, modal };
};
